import type { ComponentChildren } from 'preact';
import { parseRichText } from '../i18n';
import { Eyebrow } from './Eyebrow';

/**
 * The platform's editorial section heading triad - eyebrow + serif italic title with accent
 * `[[em]]…[[/em]]` highlights + optional muted subtitle.
 */
export interface SectionHeadingProps {
  eyebrow?: string;
  title: string;
  subtitle?: ComponentChildren;
  size?: 'display' | 'lg' | 'md' | 'sm';
  align?: 'left' | 'center';
  as?: 'h1' | 'h2' | 'h3';
  class?: string;
}

const TITLE_SIZES = {
  display: 'text-[clamp(36px,4.6vw,64px)] leading-[1.05]',
  lg:      'text-[clamp(34px,3.4vw,48px)] leading-[1.02]',
  md:      'text-[clamp(28px,2.5vw,38px)] leading-[1.1]',
  sm:      'text-[clamp(20px,1.8vw,26px)] leading-[1.2]',
};

export function SectionHeading({
  eyebrow,
  title,
  subtitle,
  size = 'lg',
  align = 'left',
  as: As = 'h1',
  class: extraClass,
}: SectionHeadingProps) {
  const segments = parseRichText(title);
  const alignClass = align === 'center' ? 'text-center mx-auto' : '';
  return (
    <div class={`space-y-2 ${alignClass} ${extraClass ?? ''}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <As
        class={`font-display font-normal tracking-[-0.01em] text-fg-primary ${TITLE_SIZES[size]}`}
      >
        {segments.map((seg, i) =>
          seg.highlighted
            ? <em key={i} class="italic text-accent">{seg.text}</em>
            : <span key={i}>{seg.text}</span>,
        )}
      </As>
      {subtitle && (
        <p class={`text-sm text-fg-secondary ${align === 'center' ? 'mx-auto' : ''} max-w-[60ch]`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}
